import React, { useEffect, useRef, useState } from 'react';
import { Offcanvas, OffcanvasHeader, OffcanvasBody, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import { Attivita } from '../../../Types/Panel.type';
import { postJson, get, del } from '../../../hook/request';

type TaskItem = {
  id?: number | string;
  descrizione: string;
  svolto: boolean;
  rapportino: boolean;
};

type TaskApi = {
  ID?: number | string;
  id?: number | string;
  descrizione: string;
  svolto: number | boolean;
  rapportino: number | boolean;
};

type TaskComment = {
  ID: number | string;
  attivitaID: number | string;
  taskID: number | string;
  descrizione: string;
  created_at: string;
  nominativo: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  attivita: Attivita | null;
  onTasksSaved?: (attivitaId: number) => void;
};

const AUTOSAVE_DEBOUNCE_MS = 500;

const AttivitaTaskDrawer: React.FC<Props> = ({ open, onClose, attivita }) => {
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskComments, setTaskComments] = useState<Record<string, string>>({});
  const [taskCommentList, setTaskCommentList] = useState<TaskComment[]>([]);
  const [sendingComments, setSendingComments] = useState<Record<string, boolean>>({});

  // ref per gestire il focus delle textarea
  const taskRefs = useRef<Array<HTMLTextAreaElement | null>>([]);

  // ---- AUTOSAVE refs
  const autosaveTimerRef = useRef<number | null>(null);
  const isHydratingRef = useRef(true); // evita autosave durante il load iniziale
  const tasksRef = useRef<TaskItem[]>([]);
  const lastSavedFingerprintRef = useRef<string>(''); // evita invii duplicati

  // helper per auto-resizzare una textarea
  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // keep ref always in sync
  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  async function loadTaskComments(attivitaId: number | string) {
    try {
      const res = await get<TaskComment[] | { data?: TaskComment[] }>(
        `/progetti/taskCommenti?attivitaID=${encodeURIComponent(String(attivitaId))}`
      );
      const payload: any = res.data;
      const rawList: TaskComment[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      setTaskCommentList(
        [...rawList].sort((a, b) =>
          (a.created_at ?? '').localeCompare(b.created_at ?? '')
        )
      );
    } catch (err) {
      console.error('Errore caricamento commenti task', err);
      toast.error('Errore nel caricamento dei commenti delle task.');
      setTaskCommentList([]);
    }
  }

  useEffect(() => {
    if (!open || !attivita?.ID) return;

    setTaskComments({});
    setTaskCommentList([]);
    setSendingComments({});

    (async () => {
      try {
        setLoadingTasks(true);
        isHydratingRef.current = true;

        const res = await get<TaskApi[] | { data?: TaskApi[] }>(
          `/progetti/task?ID=${attivita.ID}`
        );
        const payload: any = (res as any)?.data ?? res;
        const rawList: TaskApi[] = Array.isArray(payload) ? payload : [];

        if (rawList.length > 0) {
          const mapped: TaskItem[] = rawList.map(t => ({
            id: t.ID ?? t.id,
            descrizione: t.descrizione ?? '',
            svolto: t.svolto === 1 || t.svolto === true,
            rapportino: t.rapportino === 1 || t.rapportino === true,
          }));
          setTasks(mapped);
        } else {
          setTasks([{ descrizione: '', svolto: false, rapportino: false }]);
        }

        await loadTaskComments(Number(attivita.ID));
      } catch (err) {
        console.error('Errore caricamento task', err);
        toast.error('Errore nel caricamento delle task.');
        setTasks([{ descrizione: '', svolto: false, rapportino: false }]);
      } finally {
        setLoadingTasks(false);
        setTimeout(() => {
          isHydratingRef.current = false;
        }, 0);
      }
    })();

    // cleanup timer quando cambi attività / chiudi
    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [open, attivita?.ID]);

  useEffect(() => {
    taskRefs.current.forEach(autoResize);
  }, [tasks]);

  if (!attivita) return null;

  const hasTaskId = (task: TaskItem) => (
    task.id !== undefined && task.id !== null && String(task.id) !== ''
  );

  const buildPayload = (list: TaskItem[]) => {
    const cleaned = list
      .map(t => ({ ...t, descrizione: (t.descrizione ?? '').trim() }))
      .filter(t => t.descrizione.length > 0);

    return {
      cleaned,
      payload: {
        attivita: Number(attivita.ID),
        task: cleaned.map(t => ({
          ...(hasTaskId(t) ? { ID: t.id } : {}),
          descrizione: t.descrizione,
          svolto: t.svolto ? 1 : 0,
          rapportino: t.rapportino ? 1 : 0,
        })),
      },
    };
  };

  const fingerprintTasks = (list: TaskItem[]) => {
    const normalized = list
      .map(t => ({
        d: (t.descrizione ?? '').trim(),
        s: !!t.svolto,
        r: !!t.rapportino,
      }))
      .filter(t => t.d.length > 0);
    return JSON.stringify(normalized);
  };

  async function postSave(nextTasks: TaskItem[], opts?: { silent?: boolean }) {
    if (!attivita?.ID) return;

    const { cleaned, payload } = buildPayload(nextTasks);
    // if (cleaned.length === 0) {
    //   if (!opts?.silent) toast.warn('Aggiungi almeno una task con descrizione.');
    //   return;
    // }

    const fp = fingerprintTasks(nextTasks);
    if (fp === lastSavedFingerprintRef.current) return; 
    await postJson('/progetti/task', payload);
    lastSavedFingerprintRef.current = fp;
  }

  function scheduleAutosave(nextTasks: TaskItem[]) {
    if (!open || !attivita?.ID) return;
    if (isHydratingRef.current) return;

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    autosaveTimerRef.current = window.setTimeout(async () => {
      try {
        await postSave(nextTasks, { silent: true });
      } catch (err) {
        console.error('Errore autosave task', err);
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  }

  function updateTasks(updater: (prev: TaskItem[]) => TaskItem[]) {
    setTasks(prev => {
      const next = updater(prev);
      scheduleAutosave(next);
      return next;
    });
  }

  function handleChangeTask(index: number, field: keyof TaskItem, value: any) {
    updateTasks(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  }

  function handleAddTask() {
    updateTasks(prev => [
      ...prev,
      { descrizione: '', svolto: false, rapportino: false },
    ]);
  }

  function handleRemoveTask(index: number) {
      setTasks(prev => prev.filter((_, i) => i !== index));
  }

  // rimuove la task corrente (se possibile) e mette il focus su quella precedente
  function handleRemoveTaskAndFocusPrev(index: number) {
    updateTasks(prev => {
      if (prev.length <= 1) return prev;

      const copy = [...prev];
      copy.splice(index, 1);

      const prevIndex = Math.max(0, index - 1);
      setTimeout(() => {
        taskRefs.current[prevIndex]?.focus();
        autoResize(taskRefs.current[prevIndex]);
      }, 0);

      return copy;
    });
  }

  function handleInsertTaskAfter(index: number) {
    updateTasks(prev => {
      const copy = [...prev];
      const newTask: TaskItem = {
        descrizione: '',
        svolto: false,
        rapportino: false,
      };
      const newIndex = index + 1;
      copy.splice(newIndex, 0, newTask);

      setTimeout(() => {
        taskRefs.current[newIndex]?.focus();
        autoResize(taskRefs.current[newIndex]);
      }, 0);

      return copy;
    });
  }

  function handleTaskKeyDown(
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    index: number
  ) {
    const value = tasksRef.current[index]?.descrizione ?? '';
    if (e.key === 'Enter' && e.shiftKey) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      if (!value.trim()) return;
      handleInsertTaskAfter(index);
      return;
    }

    if ((e.key === 'Backspace' || e.key === 'Delete') && value.trim().length === 0) {
      e.preventDefault();
      handleRemoveTaskAndFocusPrev(index);
      return;
    }
  }

  function handleTaskChange(
    e: React.ChangeEvent<HTMLTextAreaElement>,
    index: number
  ) {
    const val = e.target.value;
    handleChangeTask(index, 'descrizione', val);

    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  async function handleSendComment(task: TaskItem) {
    if (!attivita?.ID || !hasTaskId(task)) return;

    const taskKey = String(task.id);
    const descrizione = (taskComments[taskKey] ?? '').trim();
    if (!descrizione || sendingComments[taskKey]) return;

    try {
      setSendingComments(prev => ({ ...prev, [taskKey]: true }));
      await postJson('/progetti/taskCommento', {
        attivitaID: Number(attivita.ID),
        taskID: task.id,
        descrizione,
      });
      setTaskComments(prev => ({ ...prev, [taskKey]: '' }));
      await loadTaskComments(attivita.ID);
      toast.success('Commento inviato con successo.');
    } catch (err) {
      console.error('Errore invio commento task', err);
      toast.error('Errore durante invio del commento.');
    } finally {
      setSendingComments(prev => ({ ...prev, [taskKey]: false }));
    }
  }

  async function handleDeleteTaskComment(comment: TaskComment) {
    if (!attivita?.ID) return;

    const taskKey = String(comment.taskID);
    if (sendingComments[taskKey]) return;

    try {
      setSendingComments(prev => ({ ...prev, [taskKey]: true }));
      await del(
        `/progetti/taskCommento?ID=${encodeURIComponent(String(comment.ID))}`
      );
      await loadTaskComments(attivita.ID);
      toast.success('Commento eliminato con successo.');
    } catch (err) {
      console.error('Errore eliminazione commento task', err);
      toast.error('Errore durante eliminazione del commento.');
    } finally {
      setSendingComments(prev => ({ ...prev, [taskKey]: false }));
    }
  }

  async function handleSave() {
    try {
      setSaving(true);

      // se c'è un autosave schedulato, annullalo e salva subito
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }

      await postSave(tasksRef.current, { silent: false });
      toast.success('Task salvate con successo.');
      // onTasksSaved?.(Number(attivita.ID));
    } catch (err) {
      console.error('Errore salvataggio task', err);
      toast.error('Errore nel salvataggio delle task.');
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    onClose();
  }

  // divido i task
  const tasksWithIndex = tasks.map((task, index) => ({ task, index }));
  const pendingTasks = tasksWithIndex.filter(t => !t.task.svolto);
  const doneTasks = tasksWithIndex.filter(t => t.task.svolto);
  const renderTaskId = (task: TaskItem) => {
    return (
      <span className="small text-muted text-nowrap">
        {hasTaskId(task) ? `#${task.id}` : 'Nuova'}
      </span>
    );
  };

  const formatTaskCommentDate = (value: string) => {
    const date = new Date();
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleString('it-IT');
  };

  const renderTaskComment = (task: TaskItem) => {
    if (!hasTaskId(task)) return null;

    const taskKey = String(task.id);
    const comment = taskComments[taskKey] ?? '';
    const isSending = !!sendingComments[taskKey];
    const commentsForTask = taskCommentList.filter(
      item => String(item.taskID) === taskKey
    );

    return (
      <>
        {commentsForTask.length > 0 && (
          <div className='d-flex flex-column gap-2 mt-2'>
            {commentsForTask.map(item => (
              <div
                key={item.ID}
                className='border rounded p-2'
                style={{ color: 'black', backgroundColor: '#f1f1f1' }}
              >
                <div className='d-flex justify-content-between align-items-start gap-2'>
                  <div className='small'>
                    <strong>{item.nominativo}</strong>
                    <span className='text-muted ms-2'>
                      {formatTaskCommentDate(item.created_at)}
                    </span>
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-outline-danger'
                    onClick={() => void handleDeleteTaskComment(item)}
                    disabled={isSending}
                  >
                    Elimina
                  </button>
                </div>
                <div className='mt-1'>{item.descrizione}</div>
              </div>
            ))}
          </div>
        )}

      <form
        className='input-group input-group-sm mt-2'
        onSubmit={e => {
          e.preventDefault();
          void handleSendComment(task);
        }}
      >
        <Input
          type='text'
          placeholder='Scrivi un commento'
          aria-label={`Commento per task ${task.id}`}
          value={comment}
          onChange={e =>
            setTaskComments(prev => ({ ...prev, [taskKey]: e.target.value }))
          }
          disabled={isSending}
        />
        <button
          type='submit'
          className='btn btn-primary'
          disabled={isSending || !comment.trim()}
        >
          {isSending ? 'Invio...' : 'Invia'}
        </button>
      </form>
      </>
    );
  };

  return (
    <Offcanvas
      isOpen={open}
      toggle={handleClose}
      direction="start"
      style={{ width: 450 }}
    >
      <OffcanvasHeader toggle={handleClose}>
        Task attività #{attivita.ID}
      </OffcanvasHeader>

      <OffcanvasBody>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Task</h6>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleAddTask}
            disabled={loadingTasks}
          >
            <i className="fa fa-plus me-1" />
            Task
          </button>
        </div>

        {loadingTasks && <div className="text-muted mb-3">Caricamento task...</div>}

        {!loadingTasks && tasks.length === 0 && (
          <div className="text-muted mb-3">Nessuna task. Aggiungine una.</div>
        )}

        {!loadingTasks && (
          <div className="d-flex flex-column gap-3">
            {/* Pending */}
            <div className="w-100">
              <h6 className="mb-2">Da svolgere</h6>

              {pendingTasks.length === 0 && (
                <div className="text-muted mb-2">Non ci sono task da svolgere.</div>
              )}

              <div className="d-flex flex-column gap-2 align-items-center">
                {pendingTasks.map(({ task, index }) => (
                  <div
                    key={task.id ?? index}
                    className="border rounded p-2 d-flex align-items-start gap-2 w-100"
                  >
                    <div style={{ paddingLeft: 0 }} className="form-check mt-1">
                      <div className="d-flex flex-column gap-2 align-items-center justify-content-center">
                        <Input
                          className="checkbox_animated"
                          checked={task.svolto}
                          onChange={e => handleChangeTask(index, 'svolto', e.target.checked)}
                          type="checkbox"
                        />
                        {renderTaskId(task)}
                      </div>
                    </div>
                  

                    <div className="flex-grow-1 w-100">
                      <textarea
                        ref={(el: HTMLTextAreaElement | null) => {
                          taskRefs.current[index] = el;
                          autoResize(el);
                        }}
                        rows={1}
                        className="form-control form-control-sm w-100"
                        style={{ minHeight: 30, resize: 'vertical' }}
                        placeholder="Descrizione task"
                        value={task.descrizione}
                        onChange={e => handleTaskChange(e, index)}
                        onKeyDown={e => handleTaskKeyDown(e, index)}
                      />
                      {renderTaskComment(task)}
                    </div>

                    <div className="d-flex flex-column align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveTask(index)}
                        title="Rimuovi task"
                      >
                        <i className="fa fa-trash" />
                      </button>
                      <label
                        className="d-flex flex-column align-items-start"
                        title="Rapportino"
                      >
                        <Input
                          className="checkbox_animated m-0"
                          checked={task.rapportino}
                          onChange={e =>
                            handleChangeTask(index, 'rapportino', e.target.checked)
                          }
                          type="checkbox"
                        />
                        <div className='small mt-2'>
                          Rapp.
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <hr className="my-2" />

            {/* Done */}
            <div className="w-100">
              <h6 className="mb-2">Completate</h6>

              {doneTasks.length === 0 && (
                <div className="text-muted mb-2">Non ci sono task completate.</div>
              )}

              <div className="d-flex flex-column gap-2 align-items-center">
                {doneTasks.map(({ task, index }) => (
                  <div
                    key={task.id ?? index}
                    className="border rounded p-2 d-flex align-items-start gap-2 w-100 bg-light"
                  >
                    <div style={{ paddingLeft: 0 }} className="form-check mt-1">
                      <div className="d-flex flex-column gap-2 align-items-center justify-content-center">
                        <Input
                          className="checkbox_animated"
                          checked={task.svolto}
                          onChange={e => handleChangeTask(index, 'svolto', e.target.checked)}
                          type="checkbox"
                        />
                        {renderTaskId(task)}
                      </div>
                    </div>

                    <div className="flex-grow-1 w-100">
                      <textarea
                        ref={(el: HTMLTextAreaElement | null) => {
                          taskRefs.current[index] = el;
                          autoResize(el);
                        }}
                        rows={1}
                        className="form-control form-control-sm w-100"
                        style={{ minHeight: 30, resize: 'vertical' }}
                        placeholder="Descrizione task"
                        value={task.descrizione}
                        onChange={e => handleTaskChange(e, index)}
                        onKeyDown={e => handleTaskKeyDown(e, index)}
                      />
                      {renderTaskComment(task)}
                    </div>

                    <div className="d-flex flex-column align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveTask(index)}
                        title="Rimuovi task"
                      >
                        <i className="fa fa-trash" />
                      </button>
                      <label
                        className="d-flex flex-column align-items-start"
                        title="Rapportino"
                      >
                        <Input
                          className="checkbox_animated m-0"
                          checked={task.rapportino}
                          onChange={e =>
                            handleChangeTask(index, 'rapportino', e.target.checked)
                          }
                          type="checkbox"
                        />
                        <div className='mt-2 small text-black'>
                          Rapp.
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <hr className="my-3" />

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleClose}
            disabled={saving}
          >
            Chiudi
          </button>

          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvo...' : 'Salva task'}
          </button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default AttivitaTaskDrawer;
